import type { Edge } from "@xyflow/react";
import type { NodeDefinition } from "@/generated";
import type { DynamicNodeConfig } from "@/store/editor-v2";
import type { SecretSource } from "@/components/SpotflowV2/Sidebar/useSecretsSources";
import type { NodeV2Data } from "@/types/spotflow-v2";
import type { EditorInput } from "./types";

export interface EditorResolveContext {
  nodeId?: string;
  variableBindings?: Record<string, string | undefined>;
}

export interface EditorFlowSnapshot {
  nodeId: string;
  nodeDataMap: Map<string, NodeV2Data>;
  edges: Edge[];
  nodeRegistry: NodeDefinition[];
  dynamicNodeConfigs: Map<string, DynamicNodeConfig>;
  secrets: SecretSource[];
  variableBindings?: Record<string, string | undefined>;
}

interface Entry {
  inputs: EditorInput[];
  resolveContext: EditorResolveContext | null;
  flowSnapshot: EditorFlowSnapshot | null;
  version: number;
  listeners: Set<() => void>;
}

const entries = new Map<string, Entry>();

function getEntry(editorId: string): Entry {
  let e = entries.get(editorId);
  if (!e) {
    e = {
      inputs: [],
      resolveContext: null,
      flowSnapshot: null,
      version: 0,
      listeners: new Set(),
    };
    entries.set(editorId, e);
  }
  return e;
}

function notify(entry: Entry) {
  entry.version += 1;
  entry.listeners.forEach((listener) => listener());
}

export function syncEditorStore(
  editorId: string,
  patch: {
    inputs: EditorInput[];
    resolveContext: EditorResolveContext | null;
    flowSnapshot: EditorFlowSnapshot | null;
  }
) {
  const entry = getEntry(editorId);
  entry.inputs = patch.inputs;
  entry.resolveContext = patch.resolveContext;
  entry.flowSnapshot = patch.flowSnapshot;
  notify(entry);
}

export function setInputs(editorId: string, inputs: EditorInput[]) {
  const entry = getEntry(editorId);
  entry.inputs = inputs;
  notify(entry);
}

export function setEditorResolveContext(
  editorId: string,
  context: EditorResolveContext | null
) {
  const entry = getEntry(editorId);
  entry.resolveContext = context;
  notify(entry);
}

export function setEditorFlowSnapshot(
  editorId: string,
  snapshot: EditorFlowSnapshot | null
) {
  const entry = getEntry(editorId);
  entry.flowSnapshot = snapshot;
  notify(entry);
}

export function getStoreVersion(editorId: string): number {
  return getEntry(editorId).version;
}

export function getEditorResolveContext(
  editorId: string
): EditorResolveContext | null {
  return getEntry(editorId).resolveContext;
}

export function getEditorFlowSnapshot(
  editorId: string
): EditorFlowSnapshot | null {
  return getEntry(editorId).flowSnapshot;
}

export function getInputs(editorId: string): EditorInput[] {
  return getEntry(editorId).inputs;
}

export function subscribeInputs(editorId: string, listener: () => void) {
  const entry = getEntry(editorId);
  entry.listeners.add(listener);
  return () => {
    entry.listeners.delete(listener);
  };
}

export function disposeInputs(editorId: string) {
  entries.delete(editorId);
}
